<template>
  <b-card no-body class="portlet">
    <b-card-header class="portlet-header">
      <h3 class="text-white">CRM Kanban Board</h3>
      <div class="portlet-actions">
        <button>⋮</button>
      </div>
    </b-card-header>
    <b-card-body class="portlet-body">
      <div class="kanban-wrapper">
        <div class="kanban-grid">
          <div
            v-for="column in kanbanColumns"
            :key="column.key"
            class="kanban-column"
            @dragover.prevent
            @drop="onDrop(column.key)"
          >
            <div class="kanban-column-header">
              <span>{{ column.header }}</span>
              <span class="kanban-count">{{ getColumnLeads(column.key).length }}</span>
            </div>

            <div class="kanban-column-body">
              <div
                v-for="lead in getColumnLeads(column.key)"
                :key="lead.id"
                class="kanban-card"
                draggable="true"
                @dragstart="onDragStart(lead, column.key)"
                @dragend="onDragEnd"
              >
                <div class="kanban-card-title">{{ lead.name || '-' }}</div>
                <div class="kanban-card-meta"><strong>Mobile:</strong> {{ lead.mobile || '-' }}</div>
                <div class="kanban-card-meta"><strong>Email:</strong> {{ lead.email || '-' }}</div>
                <div class="kanban-card-meta"><strong>Lead Date:</strong> {{ formatDate(lead.leaddate) }}</div>
              </div>

              <div v-if="!getColumnLeads(column.key).length" class="kanban-empty">
                No leads
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody } from 'bootstrap-vue';
import axios from 'axios';

export default {
  name: 'CrmKanban',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
  },
  data() {
    return {
      kanbanMap: {},
      baseApi: process.env.VUE_APP_APIENDPOINT,
      accessToken: localStorage.getItem('accessToken'),
      kanbanColumns: [
        { header: 'Lead', key: 'Lead' },
        { header: 'Call', key: 'Call' },
        { header: 'Connect', key: 'Connect' },
        { header: 'Interest', key: 'Interest' },
        { header: 'Visit', key: 'Visit' },
        { header: 'Meeting', key: 'Meeting' },
        { header: 'Negotiation', key: 'Negotiation' },
        { header: 'Closure', key: 'Closure' },
      ],
      draggedLead: null,
      draggedFromStage: null,
    };
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.fetchKanbanData();
      },
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    getColumnLeads(stageName) {
      const items = this.kanbanMap[stageName];
      return Array.isArray(items) ? items : [];
    },
    async fetchKanbanData() {
      try {
        const response = await axios.get(`${this.baseApi}/kanban/leads`, { params: this.$route.query || {}, headers: { 'Authorization': `Bearer ${this.accessToken}` } });
        const rawData = response.data && typeof response.data === 'object'
          ? response.data
          : (response.data && response.data.data && typeof response.data.data === 'object' ? response.data.data : {});
        const nextMap = {};

        this.kanbanColumns.forEach((column) => {
          const leadsInStage = Array.isArray(rawData[column.key]) ? rawData[column.key] : [];
          nextMap[column.key] = leadsInStage
            .filter((lead) => lead && typeof lead === 'object')
            .map((lead) => ({
              ...lead,
              id: lead.id,
              name: lead.name || '',
              mobile: lead.mobile || '',
              email: lead.email || '',
            }));
        });

        this.kanbanMap = nextMap;
      } catch (error) {
        console.error('Error fetching Kanban data:', error);
        this.$toast.error('Error fetching Kanban data.');
      }
    },
    onDragStart(lead, fromStage) {
      this.draggedLead = lead;
      this.draggedFromStage = fromStage;
    },
    onDragEnd() {
      this.draggedLead = null;
      this.draggedFromStage = null;
    },
    async onDrop(targetStage) {
      if (!this.draggedLead || !this.draggedLead.id) return;
      if (this.draggedFromStage === targetStage) {
        this.onDragEnd();
        return;
      }

      const leadId = this.draggedLead.id;
      this.onDragEnd();

      try {
        await axios.put(
          `${this.baseApi}/kanban/leads/${leadId}/stage`,
          { new_stage_name: targetStage },
          { headers: { Authorization: `Bearer ${this.accessToken}` } },
        );
      } catch (error) {
        console.error('Error updating lead stage:', error);
        this.$toast.error('Error updating lead stage.');
      } finally {
        this.fetchKanbanData();
      }
    },
  },
  created() {
    this.fetchKanbanData();
  },
};
</script>

<style>
.kanban-wrapper {
  overflow-x: auto;
  padding-bottom: 10px;
}

.kanban-grid {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(280px, 1fr);
  gap: 12px;
  align-items: start;
}

.kanban-column {
  background-color: #f0f2f5;
  border-radius: 8px;
  min-height: 380px;
  display: flex;
  flex-direction: column;
}

.kanban-column-header {
  background-color: #4e73df;
  color: #fff;
  font-weight: 700;
  border-radius: 8px 8px 0 0;
  padding: 12px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.kanban-count {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  min-width: 24px;
  text-align: center;
  padding: 2px 8px;
  font-size: 12px;
}

.kanban-column-body {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 300px;
}

.kanban-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 10px 12px;
  cursor: grab;
  border: 1px solid #e9ecef;
}

.kanban-card:active {
  cursor: grabbing;
}

.kanban-card-title {
  font-weight: 700;
  color: #0056b3;
  margin-bottom: 6px;
}

.kanban-card-meta {
  font-size: 0.9rem;
  margin-bottom: 4px;
  word-break: break-word;
}

.kanban-empty {
  color: #6c757d;
  text-align: center;
  padding: 20px;
  border: 1px dashed #ced4da;
  border-radius: 8px;
  background: #fafafa;
}

.portlet .portlet-body {
  padding: 1rem;
}

.portlet.b-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>